<script context="module" lang="ts">
    type Confirm = {
        id: number;
        fade_out: boolean;
        title: string;
        okText: string;
        cancelText: string;
        ok: () => void;
        cancel: () => void;
    } & (
        | {
              type: 'confirm';
              message: string;
          }
        | {
              type: 'prompt';
              value: string;
          }
    );

    const dialogs = writable<Confirm[]>([]);

    async function hideDialog(dialogId: number) {
        dialogs.update(list =>
            list.map(d => {
                if (d.id === dialogId) {
                    d.fade_out = true;
                }
                return d;
            })
        );
        await tick();

        setTimeout(() => {
            dialogs.update(list => list.filter(d => d.id !== dialogId));
        }, 250);
    }

    export function showConfirm(message: string, title: string, okText = 'Ok', cancelText = 'Отмена'): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            dialogs.update(v => {
                const id = v.length;
                return [
                    ...v,
                    {
                        type: 'confirm',
                        id: id,
                        fade_out: false,
                        message,
                        title,
                        okText,
                        cancelText,
                        ok: () => {
                            hideDialog(id);
                            resolve(true);
                        },
                        cancel: () => {
                            hideDialog(id);
                            resolve(false);
                        },
                    },
                ];
            });
        });
    }

    export function showPrompt(title: string, defValue = '', okText = 'Ok', cancelText = 'Отмена'): Promise<string | null> {
        return new Promise<string | null>(resolve => {
            dialogs.update(v => {
                const id = v.length;
                return [
                    ...v,
                    {
                        type: 'prompt',
                        id: id,
                        fade_out: false,
                        value: defValue,
                        title,
                        okText,
                        cancelText,
                        ok: () => {
                            // @ts-ignore
                            const value = get(dialogs).find(d => d.id === id)?.value;
                            hideDialog(id);
                            resolve(value);
                        },
                        cancel: () => {
                            hideDialog(id);
                            resolve(null);
                        },
                    },
                ];
            });
        });
    }
</script>

<script lang="ts">
    import {tick} from 'svelte';
    import {portal} from 'svelte-portal';
    import {get, writable} from 'svelte/store';

    $: is_showing = $dialogs.length > 0;

    let input: HTMLInputElement;
    $: input && input.focus();

    function onKeyDown(
        e: KeyboardEvent & {
            currentTarget: EventTarget & Document;
        }
    ) {
        const list = get(dialogs);
        if (list.length === 0) {
            return;
        }

        const dialog = list[list.length - 1];

        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            dialog.cancel();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            dialog.ok();
        }
    }
</script>

<svelte:head>
    {#if is_showing}
        <style>
            body {
                overflow: hidden;
            }
        </style>
    {/if}
</svelte:head>

<svelte:document on:keydown={onKeyDown} />

{#each $dialogs as dialog (dialog.id)}
    <div
        use:portal
        class:fadeOut={dialog.fade_out}
        class="modal-overlay fixed z-[2000] top-0 left-0 bottom-0 right-0 overflow-auto bg-black/70 backdrop-blur-sm"
    >
        <div
            class="modal-window block relative bg-white max-w-sm mt-[30vh] mb-12 ml-auto mr-auto p-4"
            on:click|stopPropagation
            aria-hidden="true"
        >
            <div class="sticky top-0 z-10 pt-1 pb-2 mb-2 text-2xl font-brand-bold">
                {dialog.title}
            </div>

            {#if dialog.type === 'confirm'}
                <div class="prose">
                    <p>{dialog.message}</p>
                </div>
            {/if}

            {#if dialog.type === 'prompt'}
                <div class="form">
                    <input type="text" class="stylized" bind:this={input} bind:value={dialog.value} />
                </div>
            {/if}

            <div class="btn-group mt-6 !justify-center">
                <!-- svelte-ignore a11y-autofocus -->
                <button type="submit" autofocus class="btn btn-green" on:click={() => dialog.ok()}>{dialog.okText}</button>
                <button type="button" class="btn btn-green-inverse" on:click={() => dialog.cancel()}>{dialog.cancelText}</button>
            </div>
        </div>
    </div>
{/each}

<style lang="postcss">
    .modal-overlay {
        animation-name: modalFadeIn;
        animation-duration: 0.3s;

        .modal-window {
            animation-name: modalWinFadeIn;
            animation-duration: 0.3s;
        }

        &.fadeOut {
            animation-name: modalFadeOut;
            animation-duration: 0.3s;

            .modal-window {
                animation-name: modalWinFadeOut;
                animation-duration: 0.3s;
            }
        }
    }

    @keyframes modalFadeIn {
        from {
            opacity: 0;
        }

        to {
            opacity: 1;
        }
    }

    @keyframes modalFadeOut {
        from {
            opacity: 1;
        }

        to {
            opacity: 0;
        }
    }

    @keyframes modalWinFadeIn {
        from {
            transform: translateY(-40px);
        }

        to {
            transform: translateY(0);
        }
    }

    @keyframes modalWinFadeOut {
        from {
            opacity: 1;
            transform: translateY(0);
        }

        to {
            opacity: 0;
            transform: translateY(40px);
        }
    }
</style>
